// src/pages/MyVendorsPage.jsx
// Upload form, list management, and vendor table for Enterprise users'
// vendor lists. Structural mirror of MyProvidersPage.jsx, trimmed for what
// vendors actually need. Match review UI (confirm/reject suggested matches)
// is NOT in this file — added once api/match-vendors.js exists.

import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useUser, useAuth } from "@clerk/react";
import { useSubscription } from "../context/SubscriptionContext";
import { useMyVendors } from "../context/MyVendorsContext";
import { useMyProviders } from "../context/MyProvidersContext";
import { authedFetch } from "../utils/apiClient";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import { searchCompanies, DEFAULT_YEAR } from "../utils/cmsApi";
import VendorScreenPanel from "../components/VendorScreenPanel";

const ROW_LIMIT = 2000;
const LIST_LIMIT = 10;
const VENDOR_PAGE_SIZE = 50;

const COL_MAP = {
  vendor_name: ["vendor name", "vendor_name", "name", "company", "supplier", "supplier name", "company name"],
  vendor_name_2: ["vendor name 2", "vendor_name_2", "vendor name2", "dba", "d/b/a", "doing business as", "also known as", "aka"],
  vendor_id: ["vendor id", "vendor_id", "internal id", "internal_id", "id", "ref id", "reference id"],
  contract_type: ["contract type", "contract_type", "type", "category"],
  department: ["department", "dept"],
  facility: ["facility", "location", "site", "hospital", "clinic"],
  notes: ["notes", "note", "comments"],
};

function normalizeHeaders(row) {
  const normalized = {};
  for (const [key, val] of Object.entries(row)) {
    const lk = key.toLowerCase().trim();
    for (const [field, aliases] of Object.entries(COL_MAP)) {
      if (aliases.includes(lk)) {
        normalized[field] = val;
        break;
      }
    }
  }
  return normalized;
}

function parseFile(file) {
  return new Promise((resolve, reject) => {
    const ext = file.name.split(".").pop().toLowerCase();
    if (ext === "csv") {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => resolve(results.data.map(normalizeHeaders)),
        error: reject,
      });
    } else if (ext === "xlsx" || ext === "xls") {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const wb = XLSX.read(e.target.result, { type: "array" });
          const ws = wb.Sheets[wb.SheetNames[0]];
          const rows = XLSX.utils.sheet_to_json(ws, { defval: "" });
          resolve(rows.map(normalizeHeaders));
        } catch (err) {
          reject(err);
        }
      };
      reader.readAsArrayBuffer(file);
    } else {
      reject(new Error("Unsupported file type. Please upload a CSV or Excel file."));
    }
  });
}

function MatchStatusBadge({ status }) {
  const styles = {
    unmatched: "bg-white/5 border-white/10 text-white/40",
    suggested: "bg-gold-500/10 border-gold-500/20 text-gold-400",
    confirmed: "bg-green-500/10 border-green-500/20 text-green-400",
    manual: "bg-teal-500/10 border-teal-500/20 text-teal-400",
    rejected: "bg-red-500/10 border-red-500/20 text-red-400",
    not_applicable: "bg-white/5 border-white/10 text-white/30",
  };
  const labels = {
    unmatched: "Unmatched", suggested: "Suggested", confirmed: "Confirmed", manual: "Manual", rejected: "Rejected",
    not_applicable: "Not Applicable",
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full border ${styles[status] || styles.unmatched}`}>
      {labels[status] || "Unmatched"}
    </span>
  );
}

function ValidationReport({ formatErrors, onConfirm, onCancel, mode }) {
  const hasIssues = formatErrors.length > 0;
  return (
    <div className="card border border-white/10 mt-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-white font-semibold text-sm">Validation Report</h3>
        {!hasIssues && <span className="text-green-400 text-xs">✓ All vendor names valid</span>}
      </div>
      {formatErrors.length > 0 && (
        <div>
          <p className="text-red-400 text-xs font-semibold mb-2">
            ✗ {formatErrors.length} row{formatErrors.length !== 1 ? "s" : ""} missing a vendor name — these will be skipped
          </p>
          <div className="max-h-40 overflow-y-auto space-y-1">
            {formatErrors.map((e, i) => (
              <div key={i} className="flex gap-3 text-xs text-white/50">
                <span className="text-white/30">Row {e.row}</span>
                <span>{e.reason}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="flex gap-3 pt-2 border-t border-white/10">
        <button onClick={onConfirm} className="btn-primary text-sm px-5 py-2">
          {mode === "delete" ? "Confirm Delete" : mode === "append" ? "Confirm Append" : "Confirm Upload"}
        </button>
        <button onClick={onCancel} className="text-white/40 hover:text-white/70 text-sm">Cancel</button>
      </div>
    </div>
  );
}

function CompanySearchBox({ onAssign, onCancel }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const data = await searchCompanies(query.trim(), DEFAULT_YEAR);
        setResults(Array.isArray(data) ? data.slice(0, 10) : []);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  return (
    <div className="bg-navy-900 border border-gold-500/20 rounded-lg p-3 mt-2 space-y-2">
      <input
        autoFocus
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search CMS companies — starts with..."
        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-white text-xs focus:outline-none focus:border-gold-500/50"
      />
      {searching && <p className="text-white/30 text-xs">Searching…</p>}
      {!searching && query.trim().length >= 2 && results.length === 0 && (
        <p className="text-white/30 text-xs">No companies found starting with "{query.trim()}"</p>
      )}
      {results.length > 0 && (
        <div className="max-h-40 overflow-y-auto space-y-1">
          {results.map((r) => (
            <button
              key={r.companyId}
              onClick={() => onAssign(r.companyId, r.companyName)}
              className="w-full text-left px-2 py-1.5 rounded hover:bg-white/5 text-xs text-white/70 hover:text-white transition-colors"
            >
              {r.companyName}
            </button>
          ))}
        </div>
      )}
      <button onClick={onCancel} className="text-white/30 hover:text-white/60 text-xs">Cancel</button>
    </div>
  );
}

const LOW_CONFIDENCE_THRESHOLD = 80;

function formatMatchMethod(method) {
  if (!method) return method;
  const labels = {
    exact: "exact", exact_nospace: "exact (no spaces)", exact_abbrev: "exact (abbreviation)", alias: "alias", fuzzy: "fuzzy", manual: "manual", mapping_reuse: "reuse", not_applicable_reuse: "reuse", rejected_reuse: "reuse",
  };
  const suffixLabels = { dba: "DBA name", combined: "combined name" };
  const [base, suffix] = method.split("_").length > 1 && suffixLabels[method.split("_").slice(-1)[0]]
    ? [method.split("_").slice(0, -1).join("_"), method.split("_").slice(-1)[0]]
    : [method, null];
  const baseLabel = labels[base] || base;
  return suffix ? `${baseLabel} (${suffixLabels[suffix]})` : baseLabel;
}

function ConfidenceLabel({ confidence, method }) {
  const low = typeof confidence === "number" && confidence < LOW_CONFIDENCE_THRESHOLD;
  return (
    <p className="text-white/30 text-[11px] flex items-center gap-1.5 justify-end">
      <span>{confidence}% · {formatMatchMethod(method)}</span>
      {low && <span className="text-amber-400/80">⚠ Low confidence</span>}
    </p>
  );
}

function PaginationControls({ page, total, pageSize, onPageChange }) {
  const lastPage = Math.max(0, Math.ceil(total / pageSize) - 1);
  return (
    <div className="flex items-center gap-2 text-xs text-white/40">
      <button
        onClick={() => onPageChange(0)}
        disabled={page === 0}
        title="First page"
        className="px-2 py-1 rounded border border-white/10 hover:border-white/30 disabled:opacity-30"
      >«</button>
      <button
        onClick={() => onPageChange(Math.max(0, page - 1))}
        disabled={page === 0}
        className="px-2 py-1 rounded border border-white/10 hover:border-white/30 disabled:opacity-30"
      >←</button>
      <span>Page {page + 1} of {lastPage + 1}</span>
      <button
        onClick={() => onPageChange(Math.min(lastPage, page + 1))}
        disabled={page >= lastPage}
        className="px-2 py-1 rounded border border-white/10 hover:border-white/30 disabled:opacity-30"
      >→</button>
      <button
        onClick={() => onPageChange(lastPage)}
        disabled={page >= lastPage}
        title="Last page"
        className="px-2 py-1 rounded border border-white/10 hover:border-white/30 disabled:opacity-30"
      >»</button>
    </div>
  );
}

function VendorTable({ vendors, onConfirm, onReject, onManualAssign, onMarkNotApplicable, onUndoNotApplicable, onUndoConfirm, providerLists, userId, getToken }) {
  const [openSearchId, setOpenSearchId] = useState(null);
  const [openScreenId, setOpenScreenId] = useState(null);

  return (
    <div className="rounded-xl border border-white/10 divide-y divide-white/5">
      {vendors.map((v) => (
        <div key={v.id} className="px-3 py-2.5">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <p className="text-white/80 text-sm font-medium truncate">{v.vendor_name}</p>
              {v.vendor_name_2 && (
                <p className="text-white/35 text-xs truncate">{v.vendor_name_2}</p>
              )}
              <p className="text-white/30 text-xs">
                {[v.vendor_id, v.contract_type, v.department, v.facility].filter(Boolean).join(" · ") || "—"}
                {v.uploaded_by_email && <span className="text-white/20"> · uploaded by {v.uploaded_by_email}</span>}
              </p>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              {v.match_status === "ambiguous" && (
                <div className="flex flex-col items-end gap-1.5">
                  <p className="text-white/40 text-[11px]">Multiple possible matches — pick one or select "Mark N/A":</p>
                  <div className="flex flex-wrap gap-1.5 justify-end max-w-md">
                    {(v.ambiguous_candidates || []).map((c) => (
                      <button
                        key={c.companyId}
                        onClick={() => onManualAssign(v.id, c.companyId, c.companyName)}
                        className="text-xs text-white/70 hover:text-white border border-white/15 hover:border-gold-500/40 rounded-full px-2.5 py-0.5 flex items-center gap-1"
                      >
                        {c.isPrimary && <span className="text-gold-400 font-medium">Assumed primary</span>}
                        {c.companyName}
                        {typeof c.confidence === "number" && <span className="text-white/30">{c.confidence}%</span>}
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setOpenSearchId(openSearchId === v.id ? null : v.id)}
                      className="text-xs text-gold-400 hover:text-gold-300 border border-gold-500/20 rounded-full px-2.5 py-0.5"
                    >
                      None of these — Search
                    </button>
                    <button
                      onClick={() => onMarkNotApplicable(v.id)}
                      className="text-xs text-white/30 hover:text-white/60"
                    >
                      Mark N/A
                    </button>
                  </div>
                </div>
              )}

              {v.match_status === "unmatched" && (
                <>
                  <MatchStatusBadge status={v.match_status} />
                  <button
                    onClick={() => setOpenSearchId(openSearchId === v.id ? null : v.id)}
                    className="text-xs text-gold-400 hover:text-gold-300 border border-gold-500/20 rounded-full px-2.5 py-0.5"
                  >
                    Search
                  </button>
                  <button
                    onClick={() => onMarkNotApplicable(v.id)}
                    className="text-xs text-white/30 hover:text-white/60"
                  >
                    Mark N/A
                  </button>
                </>
              )}

              {v.match_status === "suggested" && (
                <>
                  <div className="text-right">
                    <p className="text-white/70 text-xs">{v.matched_company_name}</p>
                    <ConfidenceLabel confidence={v.match_confidence} method={v.match_method} />
                  </div>
                  <button
                    onClick={() => onConfirm(v.id, v.matched_company_id, v.matched_company_name)}
                    className="text-xs text-green-400 hover:text-green-300 border border-green-500/20 rounded-full px-2.5 py-0.5"
                  >
                    Confirm
                  </button>
                  <button
                    onClick={() => setOpenSearchId(openSearchId === v.id ? null : v.id)}
                    className="text-xs text-gold-400 hover:text-gold-300 border border-gold-500/20 rounded-full px-2.5 py-0.5"
                  >
                    Search
                  </button>
                  <button
                    onClick={() => onReject(v.id)}
                    className="text-xs text-red-400/70 hover:text-red-400 border border-red-500/20 rounded-full px-2.5 py-0.5"
                  >
                    Reject
                  </button>
                </>
              )}

              {v.match_status === "rejected" && (
                <>
                  <div className="text-right">
                    <p className="text-white/40 text-xs line-through decoration-white/20">{v.matched_company_name}</p>
                    <ConfidenceLabel confidence={v.match_confidence} method={v.match_method} />
                  </div>
                  <MatchStatusBadge status={v.match_status} />
                  <button
                    onClick={() => setOpenSearchId(openSearchId === v.id ? null : v.id)}
                    className="text-xs text-gold-400 hover:text-gold-300 border border-gold-500/20 rounded-full px-2.5 py-0.5"
                  >
                    Search
                  </button>
                </>
              )}

              {v.match_status === "not_applicable" && (
                <>
                  <MatchStatusBadge status={v.match_status} />
                  <button
                    onClick={() => onUndoNotApplicable(v.id)}
                    className="text-xs text-white/30 hover:text-white/60"
                  >
                    Undo
                  </button>
                </>
              )}

              {(v.match_status === "confirmed" || v.match_status === "manual") && (
                <>
                  <div className="text-right">
                    <div className="flex items-center gap-2 justify-end">
                      <p className="text-white/70 text-xs">{v.matched_company_name}</p>
                      <MatchStatusBadge status={v.match_status} />
                    </div>
                    {v.confirmed_by_email && (
                      <p className="text-white/20 text-[11px]">confirmed by {v.confirmed_by_email}</p>
                    )}
                  </div>
                  <button
                    onClick={() => setOpenScreenId(openScreenId === v.id ? null : v.id)}
                    className="text-xs text-red-400/70 hover:text-red-400 border border-red-500/20 rounded-full px-2.5 py-0.5"
                  >
                    🛡 Screen
                  </button>
                  <button
                    onClick={() => onUndoConfirm(v.id)}
                    className="text-xs text-white/30 hover:text-white/60"
                  >
                    Undo
                  </button>
                  <button
                    onClick={() => setOpenSearchId(openSearchId === v.id ? null : v.id)}
                    className="text-xs text-white/30 hover:text-white/60"
                  >
                    Change
                  </button>
                </>
              )}
            </div>
          </div>
          {openSearchId === v.id && (
            <CompanySearchBox
              onAssign={(companyId, companyName) => {
                onManualAssign(v.id, companyId, companyName);
                setOpenSearchId(null);
              }}
              onCancel={() => setOpenSearchId(null)}
            />
          )}
          {openScreenId === v.id && (
            <VendorScreenPanel
              companyId={v.matched_company_id}
              companyName={v.matched_company_name}
              providerLists={providerLists}
              userId={userId}
              fetchFn={(url, opts) => authedFetch(getToken, url, opts)}
              onCancel={() => setOpenScreenId(null)}
            />
          )}
        </div>
      ))}
    </div>
  );
}

export default function MyVendorsPage() {
  const { user } = useUser();
  const { getToken } = useAuth();
  const { isEnterprise, setShowUpgradeModal, setUpgradeMessage, setUpgradeFocusTier } = useSubscription();
  const { lists, loading, activeList, activeListId, setActiveListId, refreshLists, deleteList } = useMyVendors();
  const { lists: providerLists } = useMyProviders();

  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [parsedRows, setParsedRows] = useState([]);
  const [parseError, setParseError] = useState(null);
  const [listName, setListName] = useState("My Vendors");
  const [mode, setMode] = useState("replace");
  const [validationResult, setValidationResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(null);

  const [vendors, setVendors] = useState([]);
  const [vendorTotal, setVendorTotal] = useState(0);
  const [vendorPage, setVendorPage] = useState(0);
  const [vendorSearch, setVendorSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loadingVendors, setLoadingVendors] = useState(false);
  const [matchingLoading, setMatchingLoading] = useState(false);
  const [matchingSummary, setMatchingSummary] = useState(null);
  const [bulkThresholdConfirm, setBulkThresholdConfirm] = useState(95);
  const [bulkThresholdReject, setBulkThresholdReject] = useState(70);
  const [bulkPreview, setBulkPreview] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkSummary, setBulkSummary] = useState(null);
  const loadVendors = useCallback(async (listId, page = 0, search = "", status = "") => {
    if (!user || !listId) return;
    setLoadingVendors(true);
    try {
      const params = new URLSearchParams({
        action: "getVendors", userId: user.id, listId, page: String(page), pageSize: String(VENDOR_PAGE_SIZE), search, statusFilter: status,
      });
      const res = await authedFetch(getToken, `/api/user-data?${params}`);
      const data = await res.json();
      setVendors(data.vendors || []);
      setVendorTotal(data.total || 0);
      setVendorPage(page);
    } catch {
      setVendors([]);
      setVendorTotal(0);
    } finally {
      setLoadingVendors(false);
    }
  }, [user?.id, getToken]);
  useEffect(() => {
    if (activeListId) {
      setVendorSearch("");
      setStatusFilter("");
      loadVendors(activeListId, 0, "", "");
    } else {
      setVendors([]);
      setVendorTotal(0);
    }
  }, [activeListId, loadVendors]);
  useEffect(() => {
    if (!activeListId) return;
    const t = setTimeout(() => loadVendors(activeListId, 0, vendorSearch, statusFilter), 300);
    return () => clearTimeout(t);
  }, [vendorSearch, statusFilter]);

  async function handleFile(f) {
    if (!f) return;
    setParseError(null);
    setFile(f);
    try {
      const rows = await parseFile(f);
      if (rows.length > ROW_LIMIT) {
        setParseError(`File has ${rows.length.toLocaleString()} rows, which exceeds the ${ROW_LIMIT.toLocaleString()}-vendor limit. Please reduce the file size.`);
        return;
      }
      const hasVendorName = rows.some((r) => r.vendor_name);
      if (!hasVendorName) {
        setParseError("No vendor name column found. Please ensure your file has a column named 'Vendor Name' or similar.");
        return;
      }
      setParsedRows(rows);
    } catch (err) {
      setParseError(err.message || "Failed to parse file.");
    }
  }

  function previewValidation() {
    if (!listName || !listName.trim()) {
      setParseError("List name is required.");
      return;
    }
    setParseError(null);
    const formatErrors = [];
    parsedRows.forEach((row, i) => {
      const name = String(row.vendor_name || "").trim();
      if (!name) formatErrors.push({ row: i + 2, reason: "Vendor name is empty" });
    });
    setValidationResult({ formatErrors });
  }

  async function submitUpload() {
    setUploading(true);
    try {
      const res = await authedFetch(getToken, "/api/upload-vendors", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: user.id, listName, mode, vendors: parsedRows }),
      });
      const data = await res.json();
      if (!res.ok) {
        setParseError(data.error || "Upload failed.");
        setValidationResult(null);
        return;
      }
      setUploadSuccess(data);
      setValidationResult(null);
      await refreshLists();
      setActiveListId(data.listId);
    } catch {
      setParseError("Network error. Please try again.");
    } finally {
      setUploading(false);
    }
  }

  function resetUploadForm() {
    setFile(null);
    setParsedRows([]);
    setValidationResult(null);
    setUploadSuccess(null);
    setParseError(null);
  }

  async function handleRunMatching() {
    if (!activeListId) return;
    setMatchingLoading(true);
    setMatchingSummary(null);
    try {
      const res = await authedFetch(getToken, "/api/match-vendors", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "runMatching", userId: user.id, listId: activeListId }),
      });
      const data = await res.json();
      setMatchingSummary(res.ok ? data : { error: data.error || "Matching failed." });
      await loadVendors(activeListId, vendorPage, vendorSearch, statusFilter);
    } catch {
      setMatchingSummary({ error: "Network error. Please try again." });
    } finally {
      setMatchingLoading(false);
    }
  }

  // These update the affected row in place rather than reloading the whole
  // list — the row lands in its fully-sorted position next time the list
  // reloads (page change, filter change, reopening the list), not
  // immediately on click. See conversation notes on why: sorting matches to
  // the top and "don't move the row I just clicked" are in tension, and an
  // in-place update is the version of this that doesn't jump the page.
  async function handleConfirm(vendorRowId, companyId, companyName) {
    const res = await authedFetch(getToken, "/api/match-vendors", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "confirmMatch", userId: user.id, vendorRowId, companyId, companyName }),
    });
    if (!res.ok) return;
    applyLocalStatus(vendorRowId, "confirmed", {
      matched_company_id: companyId,
      matched_company_name: companyName,
      confirmed_by_email: user.primaryEmailAddress?.emailAddress || null,
    });
  }
  async function handleReject(vendorRowId) {
    const res = await authedFetch(getToken, "/api/match-vendors", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "rejectMatch", userId: user.id, vendorRowId }),
    });
    if (!res.ok) return;
    // matched_company_id/name/confidence/method are left untouched — the
    // backend preserves them on reject now, so no local override needed.
    applyLocalStatus(vendorRowId, "rejected", {});
  }
  async function handleManualAssign(vendorRowId, companyId, companyName) {
    const res = await authedFetch(getToken, "/api/match-vendors", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "manualMatch", userId: user.id, vendorRowId, companyId, companyName }),
    });
    if (!res.ok) return;
    applyLocalStatus(vendorRowId, "confirmed", {
      matched_company_id: companyId,
      matched_company_name: companyName,
      match_confidence: 100,
      match_method: "manual",
      confirmed_by_email: user.primaryEmailAddress?.emailAddress || null,
    });
  }
  function applyLocalStatus(vendorRowId, newStatus, fields) {
    // If a status filter is active and the row no longer matches it, drop
    // it from view instead of showing a stale badge; otherwise, patch it in place.
    if (statusFilter && statusFilter !== "all" && statusFilter !== newStatus) {
      setVendors((prev) => prev.filter((v) => v.id !== vendorRowId));
      return;
    }
    setVendors((prev) =>
      prev.map((v) => (v.id === vendorRowId ? { ...v, match_status: newStatus, ...fields } : v))
    );
  }

  async function handleDeleteList(list) {
    if (!window.confirm(`Delete "${list.list_name}"? This removes all ${list.vendor_count} vendors in this list. This cannot be undone.`)) return;
    await deleteList(list.id);
  }

  async function handleMarkNotApplicable(vendorRowId) {
    try {
      await authedFetch(getToken, "/api/match-vendors", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "markNotApplicable", userId: user.id, vendorRowId }),
      });
      await loadVendors(activeListId, vendorPage, vendorSearch, statusFilter);
    } catch {
      // Silent — the row just won't update; user can retry.
    }
  }

  async function handleUndoConfirm(vendorRowId) {
    try {
      await authedFetch(getToken, "/api/match-vendors", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "undoConfirm", userId: user.id, vendorRowId }),
      });
      await loadVendors(activeListId, vendorPage, vendorSearch, statusFilter);
    } catch {
      // Silent — the row just won't update; user can retry.
    }
  }

  async function handleUndoNotApplicable(vendorRowId) {
    try {
      await authedFetch(getToken, "/api/match-vendors", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "undoNotApplicable", userId: user.id, vendorRowId }),
      });
      await loadVendors(activeListId, vendorPage, vendorSearch, statusFilter);
    } catch {
      // Silent — the row just won't update; user can retry.
    }
  }

  // Bulk actions run in two phases: preview (commit:false, returns counts,
  // writes nothing) then commit (commit:true, actually performs it). Never
  // execute a bulk mutation on the first click.
  function bulkActionName(type) {
    return type === "confirm" ? "bulkConfirm" : type === "reject" ? "bulkReject" : "bulkMarkNotApplicable";
  }

  async function handleBulkPreview(type) {
    if (!activeListId) return;
    setBulkLoading(true);
    setBulkSummary(null);
    try {
      const body = { action: bulkActionName(type), userId: user.id, listId: activeListId, commit: false };
      if (type === "confirm") body.threshold = Number(bulkThresholdConfirm);
      if (type === "reject") body.threshold = Number(bulkThresholdReject);
      const res = await authedFetch(getToken, "/api/match-vendors", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) { setBulkSummary({ error: data.error || "Preview failed." }); return; }
      setBulkPreview({ type, ...data });
    } catch {
      setBulkSummary({ error: "Network error. Please try again." });
    } finally {
      setBulkLoading(false);
    }
  }

  async function handleBulkCommit() {
    if (!activeListId || !bulkPreview) return;
    setBulkLoading(true);
    try {
      const body = { action: bulkActionName(bulkPreview.type), userId: user.id, listId: activeListId, commit: true };
      if (bulkPreview.type === "confirm") body.threshold = Number(bulkThresholdConfirm);
      if (bulkPreview.type === "reject") body.threshold = Number(bulkThresholdReject);
      const res = await authedFetch(getToken, "/api/match-vendors", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      setBulkSummary(res.ok ? { type: bulkPreview.type, ...data } : { error: data.error || "Bulk action failed." });
      setBulkPreview(null);
      await loadVendors(activeListId, 0, vendorSearch, statusFilter);
    } catch {
      setBulkSummary({ error: "Network error. Please try again." });
    } finally {
      setBulkLoading(false);
    }
  }

  function handleBulkCancel() {
    setBulkPreview(null);
  }

  if (!isEnterprise) {
    return (
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="card text-center space-y-4 py-12">
          <p className="text-2xl">🔒</p>
          <h1 className="text-white text-lg font-semibold">My Vendors — Enterprise Feature</h1>
          <p className="text-white/50 text-sm max-w-md mx-auto">
            Upload your facility's vendor roster and match it against CMS Open Payments companies to
            identify which of your providers have payment relationships with your vendors.
            Available on the Enterprise plan.
          </p>
          <button
            onClick={() => {
              setUpgradeMessage("Vendor conflict-of-interest matching is an Enterprise feature.");
              setUpgradeFocusTier("enterprise");
              setShowUpgradeModal(true);
            }}
            className="btn-primary text-sm px-5 py-2"
          >
            View Enterprise Plans
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="max-w-5xl mx-auto px-4 py-8 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-white text-xl font-semibold">My Vendors</h1>
          <p className="text-white/40 text-sm mt-1">
            Upload your facility's vendor list, match it against CMS Open Payments companies, and identify
            conflict-of-interest exposure between your vendors and your providers.
          </p>
        </div>
        <Link
          to="/reviewed-findings"
          className="flex items-center gap-2 text-sm px-4 py-2 rounded-lg border border-white/15 text-white/50 hover:text-white/80 hover:border-white/30 transition-all shrink-0"
        >
          ✓ Reviewed Findings
        </Link>
      </div>

      {lists.length > 0 && (
        <div className="card space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-white/70 text-sm font-semibold">Your Vendor Lists</h2>
            <span className="text-white/30 text-xs">{lists.length} of {LIST_LIMIT} lists used</span>
          </div>
          <div className="space-y-2">
            {lists.map((list) => (
              <div
                key={list.id}
                onClick={() => setActiveListId(list.id)}
                className={`flex items-center justify-between px-4 py-3 rounded-lg border cursor-pointer transition-all ${
                  activeListId === list.id ? "border-gold-500/40 bg-gold-500/5" : "border-white/10 hover:border-white/25"
                }`}
              >
                <div>
                  <p className="text-white/80 text-sm font-medium">{list.list_name}</p>
                  <p className="text-white/30 text-xs">
                    {(list.vendor_count || 0).toLocaleString()} vendors · updated {new Date(list.updated_at).toLocaleDateString()}
                    {list.uploaded_by_email && <> · uploaded by {list.uploaded_by_email}</>}
                  </p>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); handleDeleteList(list); }}
                  className="text-red-400/40 hover:text-red-400 text-xs transition-colors px-2"
                  title="Delete list"
                >✕</button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card space-y-5">
        <div className="flex items-start justify-between gap-4">
          <h2 className="text-white/70 text-sm font-semibold">Upload Vendor List</h2>
          <p className="text-white/20 text-xs shrink-0">Up to {ROW_LIMIT.toLocaleString()} vendors per list</p>
        </div>

        {lists.length >= LIST_LIMIT && (
          <div className="bg-gold-500/10 border border-gold-500/20 rounded-lg px-3 py-2.5">
            <p className="text-gold-400 text-xs font-medium">⚠ List limit reached — {LIST_LIMIT} of {LIST_LIMIT} lists used</p>
            <p className="text-white/40 text-xs mt-0.5">
              Delete an existing list before creating a new one, or upload to an existing list name to replace/append it.
            </p>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-white/40 text-xs mb-1 block">List name</label>
            <input
              type="text"
              value={listName}
              onChange={(e) => setListName(e.target.value)}
              maxLength={60}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-gold-500/50"
            />
          </div>
          <div>
            <label className="text-white/40 text-xs mb-1 block">Upload mode</label>
            <div className="flex items-center bg-navy-700 border border-white/10 rounded-lg overflow-hidden">
              {[{ v: "replace", label: "Replace" }, { v: "append", label: "Append" }, { v: "delete", label: "Delete" }].map(({ v, label }) => (
                <button
                  key={v}
                  onClick={() => setMode(v)}
                  className={`flex-1 text-xs px-2.5 py-2 transition-all ${mode === v ? "bg-gold-500/20 text-gold-400" : "text-white/40 hover:text-white/70"}`}
                >{label}</button>
              ))}
            </div>
          </div>
        </div>

        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]); }}
          onClick={() => document.getElementById("vendor-file-input").click()}
          className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
            dragOver ? "border-gold-500/60 bg-gold-500/5" : file ? "border-green-500/30 bg-green-500/5" : "border-white/15 hover:border-white/30"
          }`}
        >
          <input id="vendor-file-input" type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={(e) => handleFile(e.target.files[0])} />
          {file ? (
            <div>
              <p className="text-green-400 text-sm font-medium">{file.name}</p>
              <p className="text-white/40 text-xs mt-1">{parsedRows.length.toLocaleString()} rows parsed · Click to change file</p>
            </div>
          ) : (
            <div>
              <p className="text-4xl mb-3">📂</p>
              <p className="text-white/60 text-sm">Drop your CSV or Excel file here</p>
              <p className="text-white/30 text-xs mt-1">or click to browse · CSV, .xlsx supported · max {ROW_LIMIT.toLocaleString()} rows</p>
            </div>
          )}
        </div>

        <details className="text-xs text-white/40">
          <summary className="cursor-pointer hover:text-white/60 transition-colors">📋 Expected column names (click to expand)</summary>
          <div className="mt-2 grid grid-cols-2 gap-1 pl-2">
            {[["Vendor Name", "Required"], ["Vendor Name 2 / DBA", "Optional — a second name, DBA, or name continuation"], ["Vendor ID", "Optional — your internal reference"], ["Contract Type", "Optional"], ["Department", "Optional"], ["Facility", "Optional"], ["Notes", "Optional"]].map(([col, desc]) => (
              <div key={col} className="flex gap-2">
                <span className="font-mono text-white/60 min-w-[100px]">{col}</span>
                <span>{desc}</span>
              </div>
            ))}
          </div>
        </details>

        {parseError && <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">✗ {parseError}</div>}

        {parsedRows.length > 0 && !validationResult && (
          <div className="flex gap-3">
            <button onClick={previewValidation} className="btn-primary text-sm px-5 py-2">Validate {parsedRows.length.toLocaleString()} rows</button>
            <button onClick={() => { setFile(null); setParsedRows([]); setListName(""); }} className="text-white/40 hover:text-white/70 text-sm">Clear</button>
          </div>
        )}

        {validationResult && !uploadSuccess && (
          <ValidationReport formatErrors={validationResult.formatErrors} mode={mode} onConfirm={submitUpload} onCancel={() => setValidationResult(null)} />
        )}

        {uploading && <div className="text-white/50 text-sm animate-pulse">Uploading vendors…</div>}

        {uploadSuccess && (
          <div className="bg-green-500/10 border border-green-500/20 rounded-xl px-4 py-3 space-y-1">
            <p className="text-green-400 text-sm font-semibold">✓ {mode === "delete" ? "Vendors removed" : "Upload complete"}</p>
            <p className="text-white/50 text-xs">
              {mode === "delete"
                ? `${uploadSuccess.deleted} vendor${uploadSuccess.deleted !== 1 ? "s" : ""} removed · ${(uploadSuccess.totalInList ?? 0).toLocaleString()} remaining in list`
                : `${uploadSuccess.inserted} vendor${uploadSuccess.inserted !== 1 ? "s" : ""} ${mode === "append" ? "added" : "uploaded"} · ${uploadSuccess.totalInList?.toLocaleString()} total in list`}
              {uploadSuccess.formatErrors?.length > 0 && ` · ${uploadSuccess.formatErrors.length} rows skipped (missing vendor name)`}
            </p>
            <button onClick={resetUploadForm} className="text-white/40 hover:text-white/60 text-xs underline">Upload another file</button>
          </div>
        )}
      </div>

      {activeList && (
        <div className="card space-y-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h2 className="text-white/70 text-sm font-semibold">{activeList.list_name} — {vendorTotal.toLocaleString()} vendors</h2>
            <button
              onClick={handleRunMatching}
              disabled={matchingLoading}
              className="btn-primary text-xs px-4 py-1.5 disabled:opacity-50"
            >
              {matchingLoading ? "Matching…" : "Run Matching"}
            </button>
          </div>

          {matchingSummary && (
            matchingSummary.error ? (
              <p className="text-red-400 text-xs">✗ {matchingSummary.error}</p>
            ) : (
              <p className="text-white/40 text-xs">
                {matchingSummary.confirmed > 0 && `${matchingSummary.confirmed} auto-confirmed from prior matches · `}
                {matchingSummary.suggested} suggested for review · {matchingSummary.unmatched} need manual search{matchingSummary.ambiguous ? ` · ${matchingSummary.ambiguous} need disambiguation` : ""}
              </p>
            )
          )}

          <div className="bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2.5">
            <p className="text-white/50 text-xs">
              CMS Open Payments only covers drug, device, and biologic manufacturers plus group purchasing
              organizations. Most general vendors — food service, IT, facilities, staffing, office supplies — won't
              match, and that's expected. If a vendor clearly falls outside CMS's scope, mark it{" "}
              <span className="text-white/70 font-medium">Not Applicable</span> so it doesn't keep showing up for review.
            </p>
          </div>

          <div className="bg-white/[0.03] border border-white/10 rounded-lg px-3 py-3 space-y-2.5">
            <p className="text-white/50 text-xs font-medium">Bulk actions</p>

            {bulkPreview ? (
              <div className="bg-gold-500/10 border border-gold-500/20 rounded-lg px-3 py-2.5 space-y-2">
                <p className="text-white/70 text-xs">
                  {bulkPreview.type === "confirm" && (
                    <>
                      This will confirm <span className="font-semibold">{bulkPreview.eligibleCount}</span> vendors
                      {" "}({bulkPreview.byMethod?.exact || 0} exact, {bulkPreview.byMethod?.alias || 0} alias, {(bulkPreview.byMethod?.exact_nospace || 0) + (bulkPreview.byMethod?.exact_abbrev || 0)} near-exact, {bulkPreview.byMethod?.fuzzy || 0} fuzzy).
                      {bulkPreview.excludedCount > 0 && (
                        <> {bulkPreview.excludedCount} match{bulkPreview.excludedCount === 1 ? "" : "es"} on short or one-word names {bulkPreview.excludedCount === 1 ? "was" : "were"} excluded (fuzzy, no-space, or abbreviation matches only — exact and alias are never excluded) and will stay suggested for individual review.</>
                      )}
                    </>
                  )}
                  {bulkPreview.type === "reject" && (
                    <>This will reject <span className="font-semibold">{bulkPreview.eligibleCount}</span> suggested vendors below {bulkThresholdReject}% confidence. Each can still be manually reassigned via Search afterward.</>
                  )}
                  {bulkPreview.type === "notApplicable" && (
                    <>This will mark <span className="font-semibold">{bulkPreview.eligibleCount}</span> unmatched vendors as Not Applicable. Each can be individually undone afterward.</>
                  )}
                </p>
                <div className="flex gap-2">
                  <button onClick={handleBulkCommit} disabled={bulkLoading} className="btn-primary text-xs px-3 py-1.5 disabled:opacity-50">
                    {bulkLoading ? "Working…" : `Confirm — ${bulkPreview.eligibleCount || 0} vendors`}
                  </button>
                  <button onClick={handleBulkCancel} disabled={bulkLoading} className="text-white/40 hover:text-white/70 text-xs px-3 py-1.5">
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 flex-wrap">
                  <button onClick={() => handleBulkPreview("confirm")} disabled={bulkLoading} className="text-xs px-3 py-1.5 rounded-lg bg-green-500/10 text-green-400 hover:bg-green-500/20 disabled:opacity-50">
                    Confirm all suggested ≥
                  </button>
                  <input
                    type="number" min={0} max={100} value={bulkThresholdConfirm}
                    onChange={(e) => setBulkThresholdConfirm(e.target.value)}
                    className="w-16 bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs text-center focus:outline-none focus:border-gold-500/50"
                  />
                  <span className="text-white/40 text-xs">%</span>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <button onClick={() => handleBulkPreview("reject")} disabled={bulkLoading} className="text-xs px-3 py-1.5 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 disabled:opacity-50">
                    Reject all suggested &lt;
                  </button>
                  <input
                    type="number" min={0} max={100} value={bulkThresholdReject}
                    onChange={(e) => setBulkThresholdReject(e.target.value)}
                    className="w-16 bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs text-center focus:outline-none focus:border-gold-500/50"
                  />
                  <span className="text-white/40 text-xs">%</span>
                </div>
                <div>
                  <button onClick={() => handleBulkPreview("notApplicable")} disabled={bulkLoading} className="text-xs px-3 py-1.5 rounded-lg bg-white/5 text-white/60 hover:bg-white/10 disabled:opacity-50">
                    Mark all unmatched as Not Applicable
                  </button>
                </div>
              </>
            )}

            {bulkSummary && (
              bulkSummary.error ? (
                <p className="text-red-400 text-xs">✗ {bulkSummary.error}</p>
              ) : (
                <p className="text-white/40 text-xs">
                  {bulkSummary.type === "confirm" && `✓ ${bulkSummary.confirmed} vendors confirmed.${bulkSummary.excluded ? ` ${bulkSummary.excluded} short-name matches left for individual review.` : ""}`}
                  {bulkSummary.type === "reject" && `✓ ${bulkSummary.rejected} vendors rejected.`}
                  {bulkSummary.type === "notApplicable" && `✓ ${bulkSummary.markedNotApplicable} vendors marked Not Applicable.`}
                </p>
              )
            )}
          </div>

          <div className="flex gap-2">
            <div className="relative flex-1">
              <input
                type="text"
                value={vendorSearch}
                onChange={(e) => setVendorSearch(e.target.value)}
                placeholder="Search by vendor name, ID, contract type, department, facility..."
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-gold-500/50 pr-8"
              />
              {vendorSearch && (
                <button onClick={() => setVendorSearch("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 text-xs">✕</button>
              )}
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-gold-500/50"
            >
              <option value="" className="bg-navy-900 text-white">All statuses</option>
              <option value="confirmed" className="bg-navy-900 text-white">Confirmed</option>
              <option value="suggested" className="bg-navy-900 text-white">Suggested</option>
              <option value="rejected" className="bg-navy-900 text-white">Rejected</option>
              <option value="unmatched" className="bg-navy-900 text-white">Unmatched</option>
              <option value="not_applicable" className="bg-navy-900 text-white">Not Applicable</option>
              <option value="ambiguous" className="bg-navy-900 text-white">Ambiguous</option>
            </select>
          </div>

          <div className="flex items-center justify-end">
            {vendorTotal > VENDOR_PAGE_SIZE && (
              <PaginationControls
                page={vendorPage}
                total={vendorTotal}
                pageSize={VENDOR_PAGE_SIZE}
                onPageChange={(p) => loadVendors(activeListId, p, vendorSearch, statusFilter)}
              />
            )}
          </div>

          {loadingVendors ? (
            <div className="text-white/30 text-sm animate-pulse py-6 text-center">Loading vendors…</div>
          ) : vendors.length > 0 ? (
            <VendorTable
              vendors={vendors}
              onConfirm={handleConfirm}
              onReject={handleReject}
              onManualAssign={handleManualAssign}
              onMarkNotApplicable={handleMarkNotApplicable}
              onUndoNotApplicable={handleUndoNotApplicable}
              onUndoConfirm={handleUndoConfirm}
              providerLists={providerLists}
              userId={user.id}
              getToken={getToken}
            />
          ) : (
            <p className="text-white/30 text-sm text-center py-6">
              {vendorSearch ? `No vendors match "${vendorSearch}"` : "No vendors in this list yet."}
            </p>
          )}

          {vendorTotal > VENDOR_PAGE_SIZE && (
            <div className="flex items-center justify-end pt-2">
              <PaginationControls
                page={vendorPage}
                total={vendorTotal}
                pageSize={VENDOR_PAGE_SIZE}
                onPageChange={(p) => loadVendors(activeListId, p, vendorSearch, statusFilter)}
              />
            </div>
          )}
        </div>
      )}

      {loading && lists.length === 0 && <div className="text-white/30 text-sm text-center py-6">Loading your vendor lists…</div>}
    </main>
  );
}